import { useCallback, useEffect, useRef, useState } from "react";
import { apiFetch } from "../api";
import type { AdhocTransformRun } from "../configTypes";

const POLL_INTERVAL_MS = 3000;
/** SIM-2945: consecutive poll rejections tolerated before we admit we lost the run. */
const POLL_FAILURE_LIMIT = 3;
/**
 * SIM-2945: production claim latency is 1-58s, so a non-terminal row older than
 * this was abandoned by the runner. Show it, but don't poll it — otherwise one
 * stuck row disables the trigger forever.
 */
const ABANDONED_RUN_MS = 10 * 60 * 1000;

export function isTerminalAdhocStatus(status: string): boolean {
  return status === "completed" || status === "failed";
}

function isAbandonedAdhocRun(run: AdhocTransformRun): boolean {
  return Date.now() - new Date(run.requestedAt).getTime() > ABANDONED_RUN_MS;
}

export interface UseAdhocRunPollingOptions {
  /**
   * Identity of what is being run; changing it re-seeds from history. Leave
   * both fields unset to skip the mount-time read-back — the cascade drawer's
   * case, since cascade rows store `transformDefinitionId: null` and no
   * endpoint can key on them yet (SIM-3311 slice 2).
   */
  historyKey?: string;
  fetchHistory?: () => Promise<AdhocTransformRun[]>;
}

export interface AdhocRunPolling {
  latestRun: AdhocTransformRun | null;
  isPolling: boolean;
  isRunning: boolean;
  lostContact: boolean;
  /** Adopt a run this session just queued, and start polling it. */
  adoptTriggeredRun: (run: AdhocTransformRun) => void;
}

/**
 * Shared poll lifecycle for an `AdhocTransformRun` — interval, terminal check,
 * consecutive-failure bound and abandoned-run guard. Both the transform
 * editor's "Run now" and the pipeline drawer's cascade drive one of these
 * (SIM-3311); before that they were divergent copies of the same logic.
 */
export function useAdhocRunPolling(options: UseAdhocRunPollingOptions = {}): AdhocRunPolling {
  const { historyKey, fetchHistory } = options;

  const [latestRun, setLatestRun] = useState<AdhocTransformRun | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const [lostContact, setLostContact] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const failuresRef = useRef(0);
  const startedHereRef = useRef(false);

  // Held in a ref so an inline fetcher doesn't re-run the seed effect, which
  // keys on historyKey alone.
  const fetchHistoryRef = useRef(fetchHistory);
  useEffect(() => {
    fetchHistoryRef.current = fetchHistory;
  }, [fetchHistory]);

  const stopPolling = useCallback(() => {
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = null;
    setIsPolling(false);
  }, []);

  const startPolling = useCallback(
    (runId: number) => {
      stopPolling();
      failuresRef.current = 0;
      setLostContact(false);
      setIsPolling(true);
      intervalRef.current = setInterval(() => {
        void (async () => {
          try {
            const updated = await apiFetch<AdhocTransformRun>(`/api/adhoc-transform-runs/${runId}`);
            failuresRef.current = 0;
            setLatestRun(updated);
            if (isTerminalAdhocStatus(updated.status)) stopPolling();
          } catch {
            // One rejection is a blip, not a lost run. Give up only after
            // POLL_FAILURE_LIMIT in a row, and say so rather than sitting on
            // "Queued" forever, which reads identically to a runner that never
            // claimed the row.
            failuresRef.current += 1;
            if (failuresRef.current >= POLL_FAILURE_LIMIT) {
              stopPolling();
              setLostContact(true);
            }
          }
        })();
      }, POLL_INTERVAL_MS);
    },
    [stopPolling],
  );

  // SIM-2945: the outcome is always written server-side, so read it back on
  // mount — otherwise a reload inside the claim window loses it for good.
  // Reattaches the poll when the newest run is still in flight.
  useEffect(() => {
    if (historyKey == null) return;
    startedHereRef.current = false;
    let cancelled = false;
    void (async () => {
      try {
        const runs = await fetchHistoryRef.current?.();
        // A run triggered while this was in flight is newer than anything the
        // server can return here, so never let the seed overwrite it.
        if (cancelled || startedHereRef.current) return;
        const seed = runs?.[0];
        if (!seed) return;
        setLatestRun(seed);
        if (!isTerminalAdhocStatus(seed.status) && !isAbandonedAdhocRun(seed))
          startPolling(seed.id);
      } catch {
        // Leave the card empty — the pre-SIM-2945 behaviour, not a regression.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [historyKey, startPolling]);

  useEffect(() => stopPolling, [stopPolling]);

  const adoptTriggeredRun = useCallback(
    (run: AdhocTransformRun) => {
      startedHereRef.current = true;
      setLatestRun(run);
      startPolling(run.id);
    },
    [startPolling],
  );

  // Running means a live poll on a non-terminal run — not merely a non-terminal
  // status, or an abandoned row would disable the trigger for good.
  const isRunning = isPolling && latestRun != null && !isTerminalAdhocStatus(latestRun.status);

  return { latestRun, isPolling, isRunning, lostContact, adoptTriggeredRun };
}
